import styled from "styled-components";
import { Button, Typography } from "antd";
import { WHITE, GRAY_900, GRAY_200 } from "../../../constant/colors";
import { sizes, fontWeights } from "../../../constant/styles";

const { Text } = Typography;

// Floating Chat Button Wrapper for Tooltip
const FloatingChatTooltipWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Floating Chat Button Styles
const FloatingChatButtonStyle = styled(Button)`
  background: transparent;
  border: none;
  border-radius: 50px;
  height: 56px;
  width: 56px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: none;
  position: relative;

  &:hover {
    background: transparent !important;
    transform: translateY(-2px);
    box-shadow: none;
  }

  ${({ isActive }) =>
    isActive &&
    `
    background: transparent !important;
    box-shadow: none;
  `}

  &:focus {
    background: transparent !important;
    outline: none;
    border: none;
  }

  &:active {
    background: transparent !important;
    transform: translateY(0);
  }

  .anticon {
    color: ${WHITE} !important;
  }

  svg {
    stroke: ${WHITE} !important;
    fill: none !important;

    * {
      stroke: ${WHITE} !important;
      fill: none !important;
    }

    path {
      stroke: ${WHITE} !important;
      fill: none !important;
    }
  }
`;

// Floating Chat Tooltip Content
const FloatingChatTooltipContent = styled.div`
  background: ${WHITE};
  color: ${GRAY_900};
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: ${fontWeights.normal};
  padding: 10px 14px;
  border-radius: 6px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
  opacity: ${({ isHovered }) => (isHovered ? 1 : 0)};
  visibility: ${({ isHovered }) => (isHovered ? "visible" : "hidden")};
  transform: ${({ isHovered }) => (isHovered ? "translate(0, -50%)" : "translate(8px, -50%)")};
  transition: all 0.25s ease;
  pointer-events: none;
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    right: -6px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 0 6px 6px;
    border-color: transparent transparent transparent ${WHITE};
    filter: drop-shadow(1px 0 1px rgba(0, 0, 0, 0.05));
  }

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    right: -7px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 7px 0 7px 7px;
    border-color: transparent transparent transparent rgba(0, 0, 0, 0.08);
    z-index: -1;
  }
`;

// Floating Chat Panel Styles
const FloatingChatPanelStyle = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 25%;
  max-width: 400px;
  min-width: 320px;
  height: 600px;
  max-height: 70vh;
  background: ${WHITE};
  border-radius: 20px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
`;

// Floating Chat Header Styles
const FloatingChatHeaderStyle = styled.div`
  background: #5416bf;
  border-bottom: none;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  border-radius: 12px 12px 0 0;
`;

const FloatingChatHeaderActionsStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Styled wrapper for action icons with disabled state
const FloatingChatActionIconStyle = styled.div`
  width: 26px;
  height: 26px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.2s ease;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const FloatingChatTitleStyle = styled(Text)`
  color: ${WHITE};
  font-family: "Open Sans", sans-serif;
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    stroke: ${WHITE} !important;
    fill: none !important;

    * {
      stroke: ${WHITE} !important;
      fill: none !important;
    }

    path {
      stroke: ${WHITE} !important;
      fill: none !important;
    }
  }
`;

// Floating Intro Panel Styles
const FloatingIntroPanelStyle = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  box-sizing: border-box;
  background-color: ${WHITE};
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  position: relative;

  /* Ensure the DeepChat component takes full height and spans full width at bottom */
  > div {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }

  /* Ensure the input container is positioned correctly at the bottom */
  [class*="text-input-container"] {
    margin: 0 !important;
    width: 100% !important;
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }
`;

const FloatingIntroPanelHelloTextStyle = styled(Text)`
  color: ${GRAY_900};
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
  font-weight: ${fontWeights.normal};
  line-height: 22px;
  letter-spacing: 0;
  display: block;
  margin-bottom: 4px;
`;

const FloatingIntroPanelHowCanIHelpTextStyle = styled(Text)`
  color: ${GRAY_900};
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
  font-weight: ${fontWeights.normal};
  line-height: 22px;
  letter-spacing: 0;
  display: block;
`;

// Floating DeepChat Styles
const FloatingDeepChatStyle = {
  border: "none",
  height: "calc(100% - 15px)",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  flex: "1",
  minHeight: "0"
};

// Floating Text Input Styles - ensures bottom bar spans full width
const FloatingDeepChatTextInputContainerStyle = {
  background: WHITE,
  height: "58px",
  paddingLeft: "10px",
  lineHeight: "25px",
  borderRadius: "10px",
  border: `1px solid ${GRAY_200}`,
  boxShadow: "none",
  margin: "10px",
  width: "calc(100% - 20px)",
  position: "relative",
  display: "flex",
  alignItems: "center",
  fontFamily: '"Open Sans", sans-serif'
};

const FloatingDeepChatSubmitButtonStyle = {
  position: "absolute",
  right: "20px",
  top: "35px",
  transform: "translateY(-50%)",
  zIndex: 10,
  cursor: "pointer"
};

const FloatingDeepChatSubmitButtonLoadingStyle = {
  position: "absolute",
  right: "20px",
  top: "35px",
  transform: "translateY(-50%)",
  zIndex: 10,
  width: "40px",
  cursor: "pointer"
};

export {
  FloatingChatButtonStyle,
  FloatingChatTooltipWrapper,
  FloatingChatTooltipContent,
  FloatingChatPanelStyle,
  FloatingChatHeaderStyle,
  FloatingChatHeaderActionsStyle,
  FloatingChatActionIconStyle,
  FloatingChatTitleStyle,
  FloatingIntroPanelStyle,
  FloatingIntroPanelHelloTextStyle,
  FloatingIntroPanelHowCanIHelpTextStyle,
  FloatingDeepChatStyle,
  FloatingDeepChatTextInputContainerStyle,
  FloatingDeepChatSubmitButtonStyle,
  FloatingDeepChatSubmitButtonLoadingStyle
};
